import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Map,
  ArrowRight,
  Filter,
  GitBranch,
  Library,
  FileText,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  RefreshCw,
  Eye,
  Search,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Sample data showing the full flow
const SAMPLE_MAPPINGS = [
  {
    id: 'map_001',
    signals: ['late_count: 5', 'charge_off: true', 'collection: false'],
    classification: 'structural',
    method: { id: 'method_structural_r1', name: 'Round 1 Structural', round: 1 },
    prompt: { id: 'prompt_001', name: 'Structural Dispute – 15 USC 1681e(b)' },
    template: { id: 'tpl_001', name: 'Bureau Dispute Letter R1' },
    status: 'active',
  },
  {
    id: 'map_002',
    signals: ['collection: true', 'repossession: true', 'late_count: 2'],
    classification: 'recovery',
    method: { id: 'method_recovery_r1', name: 'Round 1 Recovery', round: 1 },
    prompt: { id: 'prompt_002', name: 'Recovery Account Dispute' },
    template: { id: 'tpl_002', name: 'Collection Dispute Letter' },
    status: 'active',
  },
  {
    id: 'map_003',
    signals: ['late_count: 1', 'extended_delinquency: false'],
    classification: 'isolated',
    method: { id: 'method_isolated_r1', name: 'Round 1 Isolated', round: 1 },
    prompt: { id: 'prompt_003', name: 'Isolated Late Payment Dispute' },
    template: { id: 'tpl_003', name: 'Minor Discrepancy Letter' },
    status: 'active',
  },
  {
    id: 'map_004',
    signals: ['late_count: 4', 'collection: true', 'charge_off: true'],
    classification: 'structural_recovery',
    method: { id: 'method_combo_r1', name: 'Round 1 Structural + Recovery', round: 1 },
    prompt: { id: 'prompt_004', name: 'Combined Structural Recovery' },
    template: { id: 'tpl_004', name: 'Comprehensive Dispute Letter' },
    status: 'active',
  },
  {
    id: 'map_005',
    signals: ['document_type: collection_notice', 'validation_missing: true'],
    classification: 'collection_notice',
    method: { id: 'method_collection_markup', name: 'Collection Notice Markup', round: 0 },
    prompt: { id: 'prompt_005', name: 'Collection Violation Analysis' },
    template: { id: 'tpl_005', name: 'Pre-Suit Demand Letter' },
    status: 'active',
  },
  {
    id: 'map_006',
    signals: ['round: 2', 'prior_unresolved: true', 'classification: structural'],
    classification: 'round_2',
    method: { id: 'method_round2_structural', name: 'Round 2 Structural', round: 2 },
    prompt: { id: 'prompt_006', name: 'Second Notice – Structural' },
    template: { id: 'tpl_006', name: 'Round 2 Escalation Letter' },
    status: 'draft',
  },
  {
    id: 'map_007',
    signals: ['round: 3', 'escalation: cfpb', 'classification: structural'],
    classification: 'round_3_cfpb',
    method: { id: 'method_cfpb_structural', name: 'Round 3 CFPB Structural', round: 3 },
    prompt: { id: 'prompt_007', name: 'CFPB Complaint – Structural' },
    template: { id: 'tpl_007', name: 'CFPB Complaint Form' },
    status: 'draft',
  },
];

const CLASSIFICATION_COLORS = {
  structural: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  recovery: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  isolated: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  structural_recovery: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  collection_notice: 'bg-red-500/20 text-red-400 border-red-500/30',
  round_2: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  round_3_cfpb: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
};

const MappingCard = ({ mapping, onView }) => {
  const classColor = CLASSIFICATION_COLORS[mapping.classification] || 'bg-slate-500/20 text-slate-400';
  
  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all"
      data-testid={`mapping-${mapping.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className={`px-3 py-1 text-xs rounded-full border font-medium ${classColor}`}>
          {mapping.classification.replace('_', ' + ').toUpperCase()}
        </span>
        <span className={`text-xs ${mapping.status === 'active' ? 'text-emerald-400' : 'text-amber-400'}`}>
          {mapping.status === 'active' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
        </span>
      </div>

      {/* Flow Visualization */}
      <div className="space-y-3">
        {/* Signals */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-600/20 flex items-center justify-center flex-shrink-0">
            <Filter className="text-teal-400" size={16} />
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500 mb-1">Detected Signals</p>
            <div className="flex flex-wrap gap-1">
              {mapping.signals.map((sig, i) => (
                <code key={i} className="text-xs bg-stone-800 text-teal-300 px-1.5 py-0.5 rounded font-mono">
                  {sig}
                </code>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowRight size={16} className="text-slate-600" />
        </div>

        {/* Classification */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-orange-600/20 flex items-center justify-center flex-shrink-0">
            <GitBranch className="text-orange-400" size={16} />
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500 mb-1">Classification</p>
            <span className={`px-2 py-1 text-xs rounded-full border ${classColor}`}>
              {mapping.classification.replace('_', ' ')}
            </span>
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowRight size={16} className="text-slate-600" />
        </div>

        {/* Method */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-600/20 flex items-center justify-center flex-shrink-0">
            <Library className="text-emerald-400" size={16} />
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500 mb-1">Selected Method</p>
            <p className="text-sm text-white font-medium">{mapping.method.name}</p>
            <p className="text-xs text-slate-500 font-mono">{mapping.method.id}</p>
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowRight size={16} className="text-slate-600" />
        </div>

        {/* Prompt & Template */}
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-600/20 flex items-center justify-center flex-shrink-0">
            <FileText className="text-amber-400" size={16} />
          </div>
          <div className="flex-1">
            <p className="text-xs text-slate-500 mb-1">Output</p>
            <p className="text-sm text-white">{mapping.prompt.name}</p>
            <p className="text-xs text-slate-500">{mapping.template.name}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-stone-800 flex justify-end">
        <Button variant="ghost" size="sm" onClick={() => onView(mapping)} className="text-slate-400 hover:text-white">
          <Eye size={14} className="mr-1" />
          View Details
        </Button>
      </div>
    </div>
  );
};

const StrategyMappingPage = () => {
  const { getAuthHeaders } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [selectedMapping, setSelectedMapping] = useState(null);

  // Fetch mappings from API
  const fetchMappings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rules-methods/strategy-mappings`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMappings(response.data.mappings || []);
    } catch (error) {
      console.error('Failed to fetch strategy mappings:', error);
      // Fall back to generating mappings from routing rules
      try {
        const rulesResponse = await axios.get(`${API}/rules-methods/routing-rules`, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
        const rules = rulesResponse.data.rules || [];
        
        // Generate mappings from routing rules
        const generatedMappings = rules.map((rule, index) => ({
          id: `map_${index}`,
          signals: [rule.condition],
          classification: rule.classification,
          method: { 
            id: rule.method_id, 
            name: rule.method_id?.replace('method_', '').replace(/_/g, ' ').replace('r1', 'Round 1'), 
            round: 1 
          },
          prompt: { id: `prompt_${rule.classification}`, name: `${rule.classification.replace('_', ' ')} Dispute Prompt` },
          template: { id: `tpl_${rule.classification}`, name: `${rule.classification.replace('_', ' ')} Letter Template` },
          status: rule.status,
        }));
        setMappings(generatedMappings);
      } catch (e) {
        console.error('Failed to generate mappings from rules:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMappings();
  }, []);

  const filteredMappings = mappings.filter(m => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!m.method?.name?.toLowerCase().includes(query) && 
          !m.classification?.includes(query) &&
          !(m.signals || []).some(s => s.toLowerCase().includes(query))) {
        return false;
      }
    }
    if (classFilter !== 'all' && m.classification !== classFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="strategy-mapping-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Map className="text-amber-400" />
            Strategy Mapping
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Visualize the complete flow: Signals → Classification → Method → Prompt
          </p>
        </div>
        <Button variant="outline" onClick={() => setLoading(true)} className="border-slate-700">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Flow Legend */}
      <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Execution Flow</h3>
        <div className="flex items-center gap-4 overflow-x-auto pb-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-teal-600/10 rounded-lg border border-teal-500/30">
            <Filter className="text-teal-400" size={16} />
            <span className="text-sm text-teal-300 whitespace-nowrap">1. Extract Signals</span>
          </div>
          <ArrowRight size={20} className="text-slate-600 flex-shrink-0" />
          <div className="flex items-center gap-2 px-3 py-2 bg-orange-600/10 rounded-lg border border-orange-500/30">
            <GitBranch className="text-orange-400" size={16} />
            <span className="text-sm text-orange-300 whitespace-nowrap">2. Classify</span>
          </div>
          <ArrowRight size={20} className="text-slate-600 flex-shrink-0" />
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-600/10 rounded-lg border border-emerald-500/30">
            <Library className="text-emerald-400" size={16} />
            <span className="text-sm text-emerald-300 whitespace-nowrap">3. Select Method</span>
          </div>
          <ArrowRight size={20} className="text-slate-600 flex-shrink-0" />
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-600/10 rounded-lg border border-amber-500/30">
            <FileText className="text-amber-400" size={16} />
            <span className="text-sm text-amber-300 whitespace-nowrap">4. Apply Prompt</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            placeholder="Search mappings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-stone-900 border-slate-700 text-white"
          />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-[200px] bg-stone-900 border-slate-700 text-white">
            <SelectValue placeholder="Classification" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classifications</SelectItem>
            <SelectItem value="structural">Structural</SelectItem>
            <SelectItem value="recovery">Recovery</SelectItem>
            <SelectItem value="isolated">Isolated</SelectItem>
            <SelectItem value="structural_recovery">Structural + Recovery</SelectItem>
            <SelectItem value="collection_notice">Collection Notice</SelectItem>
            <SelectItem value="round_2">Round 2</SelectItem>
            <SelectItem value="round_3_cfpb">Round 3 CFPB</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{mappings.length}</p>
          <p className="text-sm text-slate-500">Total Mappings</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-400">{mappings.filter(m => m.status === 'active').length}</p>
          <p className="text-sm text-slate-500">Active</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-400">{new Set(mappings.map(m => m.method.round)).size}</p>
          <p className="text-sm text-slate-500">Rounds Covered</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-400">{new Set(mappings.map(m => m.classification)).size}</p>
          <p className="text-sm text-slate-500">Classifications</p>
        </div>
      </div>

      {/* Mappings Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-80" />)}
        </div>
      ) : filteredMappings.length === 0 ? (
        <div className="text-center py-12 bg-stone-900 rounded-xl border border-stone-800">
          <AlertCircle size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white">No mappings found</h3>
          <p className="text-slate-400">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredMappings.map(mapping => (
            <MappingCard
              key={mapping.id}
              mapping={mapping}
              onView={setSelectedMapping}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StrategyMappingPage;
